import { useEffect, useRef, useState } from 'react';
import { NativeVideoPlayerProps } from './types';
import { API_HOST } from 'kineticUtils';
import defaultVideoPlayerStyles from 'components/VideoPlayer/styles';
import { useCustomSnackbar } from 'components/NotificationSnackbars';
import { useTranslation } from 'react-i18next';
import VideoTrimSlider from 'components/VideoTrimSlider';
import { Box } from '@mui/material';
import { useGenerateRecordingTokenUrlMutation } from '@kinetic-ui/data-layer';

type Props = Omit<NativeVideoPlayerProps, 'streamType'>;
const NativeVideoPlayer: React.FunctionComponent<Props> = (props) => {
  const { showVideoTrim, recordingDetail } = props;
  const { t } = useTranslation('common');
  const [recording, setRecording] = useState<string>();
  const { serverFileName, recordingDuration, id } = recordingDetail!;
  const [updatedDuration, setDuration] = useState<number>(recordingDuration || 0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const { video, videoWrapper } = defaultVideoPlayerStyles();
  const { enqueueCustomSnackbar } = useCustomSnackbar();
  const [generatedURL] = useGenerateRecordingTokenUrlMutation();

  const handleLoadedMetadata = () => {
    const video = videoRef.current;
    video && setDuration(video.duration);
  };

  useEffect(() => {
    const fetchRecording = async () => {
      if (!id) {
        return;
      }
      try {
        const { data } = await generatedURL({ variables: { id } });
        const tokenURL = data?.generateRecordingTokenURL;
        if (!tokenURL || !API_HOST) {
          throw new Error('Invalid recording token URL');
        }
        const hostName = API_HOST.replace(/\/$/, '');
        const recordingURL = `${hostName}${tokenURL}`;
        setRecording(recordingURL);

        const videoElement = videoRef.current;
        videoElement && videoElement.load();
      } catch (error) {
        enqueueCustomSnackbar.error(t('videoFailed'));
      }
    };

    fetchRecording();
  }, [enqueueCustomSnackbar, generatedURL, id, t]);

  return (
    <Box className={videoWrapper}>
      <video
        id='nativePlayer'
        controls
        ref={videoRef}
        className={video}
        onLoadedMetadata={handleLoadedMetadata}
        src={recording}
      />
      {showVideoTrim && (
        <VideoTrimSlider
          videoDuration={updatedDuration}
          url={recording!}
          fileName={serverFileName}
        />
      )}
    </Box>
  );
};

export default NativeVideoPlayer;
